<script setup lang="ts">
import { Ref, computed, inject, onMounted, ref } from 'vue'
import { FinalShapeDungeonHashes } from '../enums/dungeons'
import { Expansions } from '../enums/expansions'
import { EdgeOfFateSeasonHashes, FinalShapeEpisodeHashes, LightfallSeasonHashes, RenegadesSeasonHashes, WitchQueenSeasonHashes } from '../enums/seasons'
import AccountCard from '../components/AccountCard.vue'
import ActivityIndicatorExample from '../components/ActivityIndicatorExample.vue'
import { RouteLocationRaw, useRoute } from 'vue-router'
import { type Account, type Supporter, TheFooter, TheTooltip } from '@wastedondestiny/destiny-library'

const route = useRoute()
const demoAccount: Account = {
  characters: [],
  displayName: 'Example#1234',
  dungeonsOwned: [FinalShapeDungeonHashes.VespersHost, FinalShapeDungeonHashes.SunderedDoctrine],
  fireteam: [],
  membershipId: 'test1',
  membershipType: 5,
  crossSaveOverride: 3,
  seasonsOwned: [WitchQueenSeasonHashes.Seraph, LightfallSeasonHashes.Defiance, FinalShapeEpisodeHashes.Echoes, FinalShapeEpisodeHashes.Revenant,
    FinalShapeEpisodeHashes.Heresy, EdgeOfFateSeasonHashes.Reclamation, RenegadesSeasonHashes.Lawless],
  versionsOwned: Expansions.Forsaken | Expansions.Shadowkeep | Expansions.FinalShape | Expansions.EdgeOfFate | Expansions.Renegades,
  private: false
}
const dummyAccount: Account = {
  characters: [],
  displayName: 'Example#1234',
  dungeonsOwned: [],
  fireteam: [],
  membershipId: 'test2',
  membershipType: 1,
  crossSaveOverride: 3,
  seasonsOwned: [LightfallSeasonHashes.Defiance],
  versionsOwned: Expansions.Forsaken,
  private: false
}
const supporters = inject<Ref<Supporter[]>>('supporters')
const supporterLinks = computed(() => supporters?.value?.reduce((all, supporter) => {
  if (supporter.linkedMembershipIds) {
    all[supporter.name] = {
      name: 'HomePage',
      params: {
        memberships: (route.params.memberships as string[] || []).filter(x => !supporter.linkedMembershipIds?.includes(x)).concat(supporter.linkedMembershipIds)
      }
    }
  }

  return all
}, {} as Record<string, RouteLocationRaw>))

const showDemo = ref(false)

onMounted(() => {
  setTimeout(() => showDemo.value = true, 0)
})
</script>

<template>
  <div class="w-full max-w-[48rem] px-2 py-4 text-zinc-200 mx-auto pb-20">
    <h1 id="about" class="text-center text-4xl my-2 border-zinc-500 border-b pb-2">About crimson.report</h1>
    <div class="flex flex-col sm:flex-row gap-8">
      <div class="text-zinc-200">
        <p class="indent-4 text-justify">
          crimson.report is a website that aims to sort out unavailable activities between members of a group before having to go through them all.
          Too many times does one person in the group forget what they actually bought in Destiny 2 and wants to join an activity they can't play.
          With crimson.report, you can just enter their name and see what expansion, season or dungeon key they own so you can get started quicker.
        </p>
        <h2 class="text-2xl my-2 font-semibold">How to use</h2>
        <p class="indent-4 text-justify">
          Type your or a friend's Bungie name (with or without the #1234 numbers) into the search bar above to add them to the list and compare
          expansions, seasons and dungeon keys. If you want to remove a player from the list, just click the close button 
          <img src="../assets/close.svg" class="inline align-middle pb-1 h-5 w-4 invert" /> at the top right of their card. You can also add
          a user's current fireteam to the list by clicking the Add Fireteam button, if it's available.
        </p>
        <p class="mt-2 indent-4 text-justify">
          Expansions, dungeon keys and seasons allow different types of access. Furthermore, the platform on which you are playing determines which
          expansions you have. For example, if you bought the Shadowkeep expansion on Playstation, but are now playing on Steam, you will have to buy
          it again on Steam to be able to play Shadowkeep activities. Moreover, with expansions that give abilities like Lightfall,
          you will need to play on the platform where you bought the expansion to play its related abilities.
        </p>
        <p class="mt-2 indent-4 text-justify">
          Not all content is bought as a DLC. Some content, like expansions and dungeon keys, work like microtransactions and must be bought with
          in-game currency. Whether the content allows for different types of access depends on this. A rule of thumb is the following: if you have
          bought it with real money, it is linked to the platform; whereas if you bought it with Silver, it is linked to the account.
        </p>
        <p class="mt-2 indent-4 text-justify">
          crimson.report uses the following indicators to show access to content:
        </p>
        <div class="flex flex-wrap gap-2 mt-2 mx-auto w-[272px]">
          <TheTooltip other-classes="flex-shrink-0 w-[272px]">
            <ActivityIndicatorExample class="w-[272px] font-normal" state="free" text="Free to play" />
            <template #icon>
              <img src="../assets/free.svg" alt="This activity is free to play for all players" />
            </template>
            <template #content>This activity is free to play for all players</template>
          </TheTooltip>
          <TheTooltip other-classes="flex-shrink-0 w-[272px]">
            <ActivityIndicatorExample class="w-[272px] font-normal" state="everyoneHas" text="Enough people" />
            <template #icon>
              <img src="../assets/check.svg" alt="Everyone or enough people have access to this activity" />
            </template>
            <template #content>Everyone or enough people have access to this activity</template>
          </TheTooltip>
          <TheTooltip warning other-classes="flex-shrink-0 w-[272px]">
            <ActivityIndicatorExample class="w-[272px] font-normal" state="someHave" text="Not enough people" />
            <template #icon>
              <img src="../assets/warning.svg" alt="Not enough people have access to this activity" />
            </template>
            <template #content>Not enough people have access to this activity</template>
          </TheTooltip>
          <TheTooltip warning other-classes="flex-shrink-0 w-[272px]">
            <ActivityIndicatorExample class="w-[272px] font-normal" state="noOneHas" text="No access" />
            <template #icon>
              <img src="../assets/warning.svg" alt="This player does not have access to this activity" />
            </template>
            <template #content>This player does not have access to this activity</template>
          </TheTooltip>
        </div>
      </div>
      <div v-if="showDemo">
        <h2 class="text-2xl my-2 font-semibold">Demonstration</h2>
        <AccountCard
          class="mx-auto"
          :account="demoAccount"
          :accounts="[demoAccount, dummyAccount]"
          :rotations="{
            Duality: 'Active Weekly',
            'Vox Obscura': 'Active Weekly'
          }"
          :is-open="{ dungeon: true, pinnacle: false, fireteam: false, arena: false, core: true, raid: false, expansion: false, solo: false }"
          :loading="false"
          is-demo
        />
      </div>
    </div>
    <div class="text-zinc-200 text-justify mb-10">
      <h2 class="text-2xl my-2 font-semibold">Disclaimer</h2>
      <p class="indent-4">
        crimson.report is not affiliated with <a href="https://www.bungie.net" target="_blank" class="opacity-75 hover:opacity-100">Bungie</a>. Destiny
        is a registered trademark of Bungie.
      </p>
      <h2 class="text-2xl my-2 font-semibold">Supporters</h2>
      <p class="indent-4">
        crimson.report could not survive without the support of Guardians like you. If you like seeing tools like crimson.report,
        <a href="https://wastedondestiny.com" target="_blank" class="opacity-75 hover:opacity-100">Time Wasted on Destiny</a>, 
        <a href="https://loot.report" target="_blank" class="opacity-75 hover:opacity-100">loot.report</a> or
        <a href="https://guesstiny.com" target="_blank" class="opacity-75 hover:opacity-100">Guesstiny</a> exist, please
        consider <a href="http://www.patreon.com/wastedondestiny" target="_blank" class="opacity-75 hover:opacity-100">supporting me on Patreon</a>.
        Here are the top supporters:
      </p>
      <ul class="text-left list-disc ml-6">
        <li v-for="supporter in supporters">
          <RouterLink v-if="supporterLinks?.[supporter.name]" :to="supporterLinks[supporter.name]">{{ supporter.name }} ({{ supporter.tier }})</RouterLink>
          <span v-else>{{ supporter.name }} ({{ supporter.tier }})</span>
        </li>
      </ul>
      <h2 class="text-2xl my-2 font-semibold">Credits</h2>
      <p class="indent-4">
        crimson.report is made possible thanks to:
      </p>
      <ul class="text-left list-disc ml-6">
        <li><a href="https://www.bungie.com" target="_blank" class="opacity-75 hover:opacity-100">Bungie</a>, who is giving us the best game API out there</li>
        <li>All my friends and supporters for helping me crush bugs and test the website</li>
      </ul>
    </div>
    <TheFooter about name="crimson.report" use-playwire>
      <a href="https://karmageddon.games" target="_blank">&copy; Karmageddon Games {{ (new Date()).getFullYear() }}</a>
      <template #privacy>
        <RouterLink class="!decoration-1 !decoration-dotted" to="/privacy">Privacy</RouterLink>
      </template>
      <template #ad>
        <!-- <ins class="adsbygoogle w-[320px] h-[100px] sm:w-[468px] sm:h-[60px] lg:w-[728px] lg:h-[90px] block z-10 mx-auto"
          data-ad-client="ca-pub-6332373031553935"
          data-ad-slot="9971297121"
          data-ad-format="auto"
          data-full-width-responsive="true"></ins> -->
        <div id="footer-bottom" class="w-full h-[280px] mx-auto flex justify-center"></div>
      </template>
    </TheFooter>
  </div>
</template>
